import React, { useEffect, Fragment, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import * as XLSX from "xlsx";
import PropTypes from "prop-types";
import {
  clearAssessment,
  loadAssessmentsList,
  importAssessments,
  loadSchoolsList,
  deleteMultipleAssessments,
  clearResultList,
} from "../../../actions/admin";

import AssessmentList from "./AssessmentList";
import { useMemo } from "react";
import DeleteConfirmationModal from "../../layouts/DeleteConfirmationModal";
import { usePersistedTableState } from "../../../hooks/usePersistedTableState";
import AssessmentTab from "../../layouts/AssessmentTab";

const Assessments = ({
  assessmentsList,
  loadAssessmentsList,
  assessmentListLoading,
  deleteMultipleAssessments,
  clearAssessment,
  clearResultList,
  importAssessments,
  loadSchoolsList,
  schoolsList,
  user,
}) => {
  const [uniqueAssessmentList, setUniqueAssessmentList] = useState([]);
  const [groupedAssessments, setGroupedAssessments] = useState([]);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [selectedAssessments, setSelectedAssessments] = useState([]);
  const [showAppliedEffect, setShowAppliedEffect] = useState(false); // UI only
  const [isFirstAssessmentLoad, setIsFirstAssessmentLoad] = useState(
    () => !sessionStorage.getItem("assessmentsPageLoadedOnce"),
  );
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const defaultFilterState = {
    SchoolIDs: [],
    GradeIDs: [],
    LevelIDs: [],
    BenchMarkIDs: [],
    CategoryIDs: [],
  };
  const {
    page,
    setPage,
    searchQuery,
    setSearchQuery,
    persistentFilters,
    updateFilter,
    setFilters,
    selectedTab,
    setSelectedTab,
  } = usePersistedTableState("assessments", defaultFilterState);
  const [filterData, setFilterData] = useState(persistentFilters);
  const isAssessmentLoaded = useRef(false);
  const isFilterEffectFirstRun = useRef(true);
  const hasActiveFilters = (filters) =>
    Object.values(filters).some(
      (value) => Array.isArray(value) && value.length > 0,
    );

  const groupAssessmentsByCreatedDate = (assessments) => {
    if (!assessments?.length) return [];

    const sorted = [...assessments].sort(
      (a, b) => new Date(a.CreatedDate) - new Date(b.CreatedDate),
    );

    return sorted.reduce((groups, assessment) => {
      if (groups.length === 0) {
        groups.push([assessment]);
        return groups;
      }

      const currentGroup = groups[groups.length - 1];
      const firstAssessment = currentGroup[0];

      const differenceInSeconds =
        (new Date(assessment.CreatedDate) -
          new Date(firstAssessment.CreatedDate)) /
        1000;

      if (differenceInSeconds <= 1) {
        currentGroup.push(assessment);
      } else {
        groups.push([assessment]);
      }

      return groups;
    }, []);
  };

  const getFirstSchoolID = () => {
    const schoolIds = user?.schoolIds
      ? String(user.schoolIds)
          .split(",")
          .map((id) => id.trim())
      : [];

    if (schoolIds.length > 0 && schoolIds[0]) {
      return [Number(schoolIds[0])];
    }

    return user?.SchoolID ? [user.SchoolID] : [];
  };
  // 1. Initial Load & Sync Default School when user data resolves
  useEffect(() => {
    const firstSchoolID = getFirstSchoolID();
    if (
      firstSchoolID.length === 0 &&
      (!schoolsList || schoolsList.length === 0)
    ) {
      return;
    }
    // Prevent duplicate API calls on same page load
    if (isAssessmentLoaded.current) {
      return;
    }
    const defaultSchools =
      firstSchoolID.length > 0
        ? firstSchoolID
        : schoolsList?.length > 0
          ? [schoolsList[0].SchoolID]
          : [];
    isAssessmentLoaded.current = true;
    setFilterData((prev) => ({
      ...prev,
      SchoolIDs: prev.SchoolIDs.length > 0 ? prev.SchoolIDs : defaultSchools,
    }));

    // // Dispatch initial fetch with the first school default
    // loadAssessmentsList({
    //   ...filterData,
    //   SchoolIDs: defaultSchools,
    // });
  }, [user?.schoolIds, user?.SchoolID, schoolsList]); // Syncs when auth state loads

  useEffect(() => {
    const currentFilters = JSON.stringify(filterData);
    const persistedFiltersString = JSON.stringify(persistentFilters);

    if (currentFilters !== persistedFiltersString) {
      setFilterData(persistentFilters);
    }
  }, [persistentFilters]);

  useEffect(() => {
    const currentFilters = JSON.stringify(filterData);
    const persistedFiltersString = JSON.stringify(persistentFilters);

    if (currentFilters !== persistedFiltersString) {
      setFilters(filterData);
    }
  }, [filterData, persistentFilters, setFilters]);

  useEffect(() => {
    if (!hasActiveFilters(filterData)) return;

    const shouldShowFilterLoading = !isFilterEffectFirstRun.current;
    if (isFilterEffectFirstRun.current) {
      isFilterEffectFirstRun.current = false;
    }

    if (shouldShowFilterLoading) {
      setIsFilterLoading(true);
    }

    let isActive = true;
    const timeoutId = setTimeout(() => {
      loadAssessmentsList({
        ...filterData,
      }).finally(() => {
        if (isActive && shouldShowFilterLoading) {
          setIsFilterLoading(false);
        }
      });
    }, 1000);
    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [filterData]);

  useEffect(() => {
    if (!assessmentListLoading && isFirstAssessmentLoad) {
      setIsFirstAssessmentLoad(false);
      sessionStorage.setItem("assessmentsPageLoadedOnce", "true");
    }
  }, [assessmentListLoading, isFirstAssessmentLoad]);
  // useEffect(() => {
  //   // Always fetch fresh assessment list when component mounts
  //   if (user.UserTypeID === 6) {
  //     loadAssessmentsList({ ...filterData, schoolIds: user.schoolIds });
  //   } else {
  //     loadAssessmentsList({ ...filterData, schoolIds: null });
  //   }
  //   // Admin role: Retrive all assessment from the Admin's school list

  //   // Super Admin role and other roles: Retrive all assessments from all schools
  // }, [user.UserTypeID, user.schoolIds, loadAssessmentsList, filterData.BenchMarkIDs, filterData.GradeIDs, filterData.SchoolIDs]);

  // Remove duplicate assessments for Admin role and super admin role
  useEffect(() => {
    let uniqueAssessmentList = [];

    if (assessmentsList.length > 0) {
      if (user.UserTypeID === 6) {
        uniqueAssessmentList = assessmentsList
          .filter((assessment) =>
            user.schoolIds
              .split(",")
              .map((id) => parseInt(id.trim()))
              .includes(assessment.SchoolID),
          )
          .reduce((acc, assessment) => {
            if (
              !acc.some((item) => item.AssessmentID === assessment.AssessmentID)
            ) {
              acc.push(assessment);
            }
            return acc;
          }, []);
      } else {
        uniqueAssessmentList = assessmentsList.reduce((acc, assessment) => {
          if (
            !acc.some((item) => item.AssessmentID === assessment.AssessmentID)
          ) {
            acc.push(assessment);
          }
          return acc;
        }, []);
      }

      if (selectedTab === "assessments") {
        uniqueAssessmentList = uniqueAssessmentList.filter(
          (items) =>
            items.IsProgressMonitoring == null ||
            items.IsProgressMonitoring == 0,
        );
      } else {
        uniqueAssessmentList = uniqueAssessmentList.filter(
          (items) =>
            items.IsProgressMonitoring == true ||
            items.IsProgressMonitoring == 1,
        );
      }
    }

    setUniqueAssessmentList(uniqueAssessmentList);

    // Always sync groupedAssessments — even to empty — instead of only inside the `if`
    setGroupedAssessments(
      selectedTab === "assessments"
        ? []
        : groupAssessmentsByCreatedDate(uniqueAssessmentList),
    );
  }, [assessmentsList, selectedTab, user]);

  const handleApplyFilter = () => {
    const payload = {
      ...filterData,
      // Fallback to user default schools if no explicit school selected in filter UI
      SchoolIDs:
        filterData.SchoolIDs.length > 0
          ? filterData.SchoolIDs
          : getFirstSchoolID(),
    };
    setFiltersApplied(true);
    setIsFilterLoading(true);
    loadAssessmentsList(payload).finally(() => setIsFilterLoading(false));
    // Temporary success effect
    setShowAppliedEffect(true);

    setTimeout(() => {
      setShowAppliedEffect(false);
    }, 1000);
  };
  const handleClearFilter = () => {
    const firstSchoolID = getFirstSchoolID();
    const defaultSchools =
      firstSchoolID.length > 0
        ? firstSchoolID
        : schoolsList?.length > 0
          ? [schoolsList[0].SchoolID]
          : [];
    setFilterData({
      SchoolIDs: defaultSchools,
      GradeIDs: [],
      BenchMarkIDs: [],
      CategoryIDs: [],
    });
    setFiltersApplied(false);
  };

  const showAssessmentLoader =
    (assessmentListLoading && isFirstAssessmentLoad) || isFilterLoading;

  const [file, setFile] = useState(null);
  const hist = useHistory();

  return (
    <Fragment>
      <div className="p-sm-5 p-2 w-100 dashboard-margin">
        <div className="mb-3 ">
          <div className="d-flex align-items-center ">
            <h6 className="txt-primary-light mb-0">
              {user.UserType} / Library
            </h6>{" "}
            <div className="rounded-pill bg-primary px-2 py-1 align-self-center mx-2 my-2 caption ">
              {uniqueAssessmentList?.length}
            </div>
          </div>
          <div className="d-flex w-100 align-items-center justify-content-end">
            {user.UserTypeID === 5 && (
              <div className="d-flex">
                {/* <div className="btn button-parent button-primary d-flex align-items-center px-3"> */}
                {selectedAssessments.length > 0 && (
                  <button
                    className="btn btn-danger d-flex align-items-center px-3 mx-3"
                    data-bs-toggle="modal"
                    data-bs-target="#deleteMutipleAssessments"
                    // onClick={() => handleBulkDelete(selectedSchools)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      className="bi bi-trash-fill button-child me-2"
                      viewBox="0 0 16 16"
                    >
                      <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1H2.5z" />
                    </svg>
                    Delete ({selectedAssessments.length})
                  </button>
                )}
                {/* </div> */}
                <a
                  href={`${process.env.PUBLIC_URL}/templates/assessments${
                    user.UserTypeID === 5 ? "_admin" : ""
                  }.zip`}
                  download
                  className="btn button-parent button-primary d-flex align-items-center px-3 mx-3"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    class="bi bi-download button-child"
                    viewBox="0 0 16 16"
                  >
                    <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                    <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                  </svg>
                  Template
                </a>
                <div
                  className="btn button-parent button-primary d-flex align-items-center px-3 mx-3"
                  data-bs-toggle="modal"
                  data-bs-target="#ImportUser"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    enableBackground="new 0 0 24 24"
                    height="18px"
                    viewBox="0 0 24 24"
                    width="24px"
                    fill="#ffffff"
                    className="button-child"
                  >
                    <g>
                      <rect fill="none" height="18" width="18" />
                    </g>
                    <g>
                      <g>
                        <path d="M19,13h-6v6h-2v-6H5v-2h6V5h2v6h6V13z" />
                      </g>
                    </g>
                  </svg>
                  Import Excel
                </div>
                <div
                  className="btn button-parent button-primary d-flex align-items-center px-3"
                  onClick={() => {
                    loadSchoolsList().then(() => {
                      clearAssessment();
                      hist.push("/admin/assessment/add");
                    });
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    enableBackground="new 0 0 24 24"
                    height="18px"
                    viewBox="0 0 24 24"
                    width="24px"
                    fill="#ffffff"
                    className="button-child"
                  >
                    <g>
                      <rect fill="none" height="18" width="18" />
                    </g>
                    <g>
                      <g>
                        <path d="M19,13h-6v6h-2v-6H5v-2h6V5h2v6h6V13z" />
                      </g>
                    </g>
                  </svg>
                  Add Assessment
                </div>
              </div>
            )}
          </div>
        </div>
        <AssessmentTab
          onTabChange={(value) => setSelectedTab(value)}
          selectedTab={selectedTab}
        />
        <AssessmentList
          assessmentsList={
            selectedTab !== "assessments"
              ? groupedAssessments
              : uniqueAssessmentList?.length > 0
                ? uniqueAssessmentList
                : []
          }
          selectedTab={selectedTab}
          assessmentListLoading={assessmentListLoading}
          showLoader={showAssessmentLoader}
          user={user}
          clearResultList={clearResultList}
          filterData={filterData}
          setFilterData={setFilterData}
          handleApplyFilter={handleApplyFilter}
          handleClearFilter={handleClearFilter}
          filtersApplied={filtersApplied}
          showAppliedEffect={showAppliedEffect}
          selectedAssessments={selectedAssessments}
          setSelectedAssessments={setSelectedAssessments}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          persistentFilters={persistentFilters}
          page={page}
          setPage={setPage}
        />
      </div>
      <DeleteConfirmationModal
        id="deleteMutipleAssessments"
        title="Delete Assessments(s)"
        message="Are you sure you want to delete Assessment(s)?"
        warning="Deleting a Assessment will result in deleting everything related to it."
        onConfirm={() => {
          // deleteSchool(SchoolID);
          // hist.push("/admin/schools");
          deleteMultipleAssessments(selectedAssessments, filterData);
          clearResultList();
          setSelectedAssessments([]);
          // clearSchool();
        }}
      />
      <div
        className="modal fade"
        id="ImportUser"
        aria-labelledby="ImportUserLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="ImportUserLabel">
                Import Assessments
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <input
                type="file"
                accept=".xlsx"
                onChange={(e) => {
                  e.preventDefault();
                  var files = e.target.files,
                    f = files[0];
                  var reader = new FileReader();
                  reader.onload = function (e) {
                    var data = e.target.result;
                    let readedData = XLSX.read(data, { type: "binary" });
                    const wsname = readedData.SheetNames[0];
                    const ws = readedData.Sheets[wsname];

                    /* Convert array to json*/
                    const dataParse = XLSX.utils.sheet_to_json(ws, {
                      header: 1,
                    });
                    dataParse.splice(0, 1);

                    let d = [];
                    dataParse.forEach((assessment) => {
                      if (assessment.length !== 0) {
                        if (parseInt(assessment[2]) > 4) {
                          d.push({
                            Title: assessment[0],
                            GradeID: assessment[1],
                            CategoryID: assessment[2],
                            Timer: assessment[3],
                            Body: assessment[4],
                            Solution: assessment[5],
                            SolutionDetails:
                              assessment[6] === undefined
                                ? null
                                : assessment[6],
                            Scores: assessment[7],
                            SchoolsID:
                              user.SchoolID === null
                                ? assessment[8].split(",")
                                : [user.SchoolID],
                          });
                        } else {
                          if (parseInt(assessment[2]) === 3) {
                            assessment[4] = assessment[4]
                              .split(" ")
                              .join("&nbsp;");
                          }
                          d.push({
                            Title: assessment[0],
                            GradeID: assessment[1],
                            CategoryID: assessment[2],
                            Timer: assessment[3],
                            Body: assessment[4],
                            Groups: assessment[5],
                            SchoolsID:
                              user.SchoolID === null
                                ? assessment[6].split(",")
                                : [user.SchoolID],
                          });
                        }
                      }
                    });

                    setFile(d);
                  };
                  reader.readAsBinaryString(f);
                }}
              />
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn button-primary"
                onClick={() => {
                  importAssessments({ assessments: file });

                  setFile(null);
                }}
                data-bs-dismiss="modal"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

Assessments.propTypes = {
  assessmentsList: PropTypes.array,
  loadAssessmentsList: PropTypes.func.isRequired,
  deleteMultipleAssessments: PropTypes.func.isRequired,
  assessmentListLoading: PropTypes.bool,
  clearAssessment: PropTypes.func.isRequired,
  clearResultList: PropTypes.func.isRequired,
  importAssessments: PropTypes.func.isRequired,
  loadSchoolsList: PropTypes.func.isRequired,
  user: PropTypes.object,
};
const mapStateToProps = (state) => ({
  assessmentsList: state.admin.assessmentsList,
  assessmentListLoading: state.admin.assessmentListLoading,
  user: state.auth.user,
  schoolsList: state.admin.schoolsList,
});

export default connect(mapStateToProps, {
  clearAssessment,
  loadAssessmentsList,
  importAssessments,
  deleteMultipleAssessments,
  clearResultList,
  loadSchoolsList,
})(Assessments);
